/**
 * Utility functions for working with tweets
 */

import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { regex } from 'arkregex';
import type { LinkMetadata } from '@/lib/fetch-metadata';
import { canonicalKeyForComparison } from '@/lib/url/normalize';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TweetEntities } from '@/types/tweet';
import { isTweetUrl as isTweetUrlImpl } from './tweet-url-utils';
import { extractUrls, getTweetLength as getTweetLengthAdapter, validateTweet } from './twitter-text-adapter';

export const isTweetUrl = (...parameters: Parameters<typeof isTweetUrlImpl>) => isTweetUrlImpl(...parameters);

const WWW_PREFIX_PATTERN = /^www\./i;

export interface ExtractedTweetUrl {
  tweetId: string;
  url: string;
  username: string;
}

const TWEET_URL_PATTERN = regex.as(
  String.raw`^https?:\/\/(?:www\.|mobile\.)?(twitter\.com|x\.com)\/([^/]+)\/status\/([\d_]+)`,
  'i'
);
const TWEET_I_WEB_PATTERN = regex.as(
  String.raw`^https?:\/\/(?:www\.|mobile\.)?(twitter\.com|x\.com)\/i\/(?:web\/)?status\/([\d_]+)`,
  'i'
);
const TWEET_MEDIA_SUFFIX_PATTERN = regex.as(String.raw`/photo/\d+$|/video/\d+$`);
const COLLAPSE_WHITESPACE_PATTERN = regex.as('\\s+', 'g');

/**
 * Extracts tweet IDs from Twitter/X URLs
 * Supports various formats:
 * - https://twitter.com/username/status/1_234_567_890
 * - https://x.com/username/status/1_234_567_890
 * - https://mobile.twitter.com/username/status/1_234_567_890
 * - https://www.twitter.com/username/status/1_234_567_890
 *
 * Filters out media URLs (pic.twitter.com, pic.x.com) which are image attachments, not tweet references
 */
export function extractTweetUrls(
  urls: { url: string; expanded_url?: string; display_url?: string }[]
): ExtractedTweetUrl[] {
  const tweetUrls: ExtractedTweetUrl[] = [];

  for (const urlObject of urls) {
    // Skip if this is a media URL (pic.twitter.com or pic.x.com)
    // These URLs appear when images are attached to tweets
    if (urlObject.display_url?.startsWith('pic.twitter.com') || urlObject.display_url?.startsWith('pic.x.com')) {
      continue;
    }

    const urlToCheck = urlObject.expanded_url || urlObject.url;
    if (!urlToCheck) {
      continue;
    }

    // Additional check: Skip URLs that end with /photo/1, /video/1, etc. as these are media pages
    if (TWEET_MEDIA_SUFFIX_PATTERN.test(urlToCheck)) {
      continue;
    }

    const match = TWEET_URL_PATTERN.exec(urlToCheck);
    if (match?.[2] && match[3]) {
      const tweetId = match[3].replaceAll('_', '');
      tweetUrls.push({ url: urlToCheck, username: match[2], tweetId });
      continue;
    }
    const matchI = TWEET_I_WEB_PATTERN.exec(urlToCheck);
    if (matchI?.[2]) {
      const tweetId = matchI[2].replaceAll('_', '');
      tweetUrls.push({ url: urlToCheck, username: 'i', tweetId });
    }
  }

  return tweetUrls;
}

/**
 * Removes tweet URLs from text
 * Used when we display the tweet inline and don't want to show the URL
 */
export function removeTweetUrlsFromText(
  text: string,
  urls: { url: string; expanded_url?: string; display_url?: string }[]
): string {
  let processedText = text;
  const tweetUrls = extractTweetUrls(urls);

  // Remove each tweet URL from the text
  for (const tweetUrl of tweetUrls) {
    // Find the original t.co URL in the text
    const matchingUrl = urls.find((u) => (u.expanded_url || u.url) === tweetUrl.url);
    if (matchingUrl?.url) {
      processedText = processedText.replaceAll(matchingUrl.url, '').trim();
    }
  }

  // Clean up extra whitespace
  processedText = processedText.replaceAll(COLLAPSE_WHITESPACE_PATTERN, ' ').trim();

  return processedText;
}

/**
 * Checks if a URL is a tweet URL
 */
// isTweetUrl moved to ./tweet-url-utils

/**
 * Extract all URLs from tweet text using twitter-text library
 * More robust than regex-based extraction
 */
export function extractUrlsFromText(text: string): { url: string; indices: [number, number] }[] {
  const urls = extractUrls(text);
  return Array.isArray(urls) ? urls : [];
}

/**
 * Validate tweet text length and content using twitter-text
 */
export function validateTweetText(text: string): { valid: boolean; length: number } {
  const result = validateTweet(text);
  return {
    valid: result.valid,
    length: result.weightedLength,
  };
}

/**
 * Prepares tweet text for AI analysis by expanding t.co URLs and creating a display version
 *
 * This function serves a dual purpose for AI analysis workflows:
 * 1. Expands t.co shortened URLs to their full destinations so AI can understand what's being linked
 * 2. Creates a display version with bracketed domains [example.com] for human-readable output
 *
 * @param text The tweet text containing t.co URLs
 * @param entities The entities object from the tweet containing URL mappings
 * @returns Object with:
 *   - expanded: Text with full URLs for AI processing
 *   - display: Text with bracketed domain names for human readability
 *
 * @example
 * // Input: "Check this out: https://t.co/abc123"
 * // Output: {
 * //   expanded: "Check this out: https://github.com/anthropics/claude",
 * //   display: "Check this out: [github.com/anthropics/claude]"
 * // }
 */
export function prepareTweetTextForAnalysis(
  text: string,
  entities: TweetEntities | null | undefined,
  metadata?: Record<string, LinkMetadata> | null
): { expanded: string; display: string } {
  let expandedText = text;
  let displayText = text;

  const metadataLookup = metadata && Object.keys(metadata).length > 0 ? buildMetadataLookup(metadata) : null;

  if (entities?.urls && Array.isArray(entities.urls)) {
    // Sort URLs by start position in reverse to avoid position shifting
    const sortedUrls = toSortedCompat([...entities.urls], (a, b) => (b.start || 0) - (a.start || 0));

    for (const urlEntity of sortedUrls) {
      if (!urlEntity.url) {
        continue;
      }
      const longUrl = urlEntity.unwoundUrl ?? urlEntity.expandedUrl ?? urlEntity.url;
      const linkMetadata =
        metadataLookup?.(longUrl) ?? metadataLookup?.(urlEntity.expandedUrl) ?? metadataLookup?.(urlEntity.url);
      const description = pickLinkDescription(linkMetadata, urlEntity.description);
      const isMediaOnlyLink = isPhotoOrVideoAttachment(longUrl, urlEntity.displayUrl);

      // Replace t.co URL with expanded URL plus optional description for AI processing
      let expandedReplacement = longUrl;
      if (isMediaOnlyLink) {
        expandedReplacement = '';
      } else if (description && !hasInlineDescription(longUrl, description)) {
        expandedReplacement = `${longUrl} — ${description}`;
      }
      expandedText = expandedText.replaceAll(urlEntity.url, expandedReplacement);

      // For display version, use displayUrl if available, otherwise use domain
      const displayLabel = resolveDisplayLabel(urlEntity.displayUrl, longUrl);
      let displayReplacement = `[${displayLabel}]`;
      if (isMediaOnlyLink) {
        displayReplacement = '';
      } else if (description) {
        displayReplacement = `[${displayLabel}] — ${description}`;
      }
      displayText = displayText.replaceAll(urlEntity.url, displayReplacement);
    }
  }

  expandedText = stripMediaLinks(expandedText);
  displayText = stripMediaLinks(displayText);

  return {
    expanded: cleanupWhitespace(expandedText),
    display: cleanupWhitespace(displayText),
  };
}

// Keep link metadata blurbs concise so each expanded URL only adds ~100 chars to the prompt payload.
const DESCRIPTION_MAX_LENGTH = 100;

const sanitizeDescription = (raw: string | null | undefined): string | null => {
  if (typeof raw !== 'string') {
    return null;
  }
  const collapsed = raw.replaceAll(/\s+/g, ' ').trim();
  if (!collapsed) {
    return null;
  }
  const withoutBrackets = collapsed.replaceAll(/[[\]]/g, '');
  if (withoutBrackets.length > DESCRIPTION_MAX_LENGTH) {
    return `${withoutBrackets.slice(0, DESCRIPTION_MAX_LENGTH - 1)}…`;
  }
  return withoutBrackets;
};

const hasInlineDescription = (longUrl: string, description: string): boolean => {
  const normalizedUrl = toTrimmedNonEmptyString(longUrl)?.toLowerCase() ?? '';
  const normalizedDescription = toTrimmedNonEmptyString(description)?.toLowerCase() ?? '';
  if (!(normalizedUrl && normalizedDescription)) {
    return false;
  }
  return normalizedUrl.includes(normalizedDescription);
};

const resolveDisplayLabel = (displayUrl: string | undefined, fallbackUrl: string): string => {
  const trimmed = toTrimmedNonEmptyString(displayUrl);
  if (trimmed) {
    return trimmed;
  }
  try {
    const parsed = new URL(fallbackUrl);
    return parsed.hostname.replace(WWW_PREFIX_PATTERN, '');
  } catch {
    return fallbackUrl;
  }
};

const buildMetadataLookup = (
  metadata: Record<string, LinkMetadata>
): ((candidate: string | null | undefined) => LinkMetadata | undefined) => {
  const canonicalMap = new Map<string, LinkMetadata>();
  for (const [key, value] of Object.entries(metadata)) {
    if (!value) {
      continue;
    }
    canonicalMap.set(key, value);
    const canonicalKey = canonicalKeyForComparison(key);
    if (canonicalKey) {
      canonicalMap.set(canonicalKey, value);
    }
    if (value.url) {
      canonicalMap.set(value.url, value);
      const canonicalFromValue = canonicalKeyForComparison(value.url);
      if (canonicalFromValue) {
        canonicalMap.set(canonicalFromValue, value);
      }
    }
  }

  return (candidate: string | null | undefined) => {
    if (!candidate) {
      return;
    }
    if (metadata[candidate]) {
      return metadata[candidate];
    }
    const canonicalKey = canonicalKeyForComparison(candidate);
    if (canonicalKey && canonicalMap.has(canonicalKey)) {
      return canonicalMap.get(canonicalKey);
    }
    return;
  };
};

const pickLinkDescription = (
  metadata: LinkMetadata | undefined,
  entityDescription: string | null | undefined
): string | null => {
  const metadataDescription = sanitizeDescription(metadata?.description);
  if (metadataDescription) {
    return metadataDescription;
  }
  const metadataTitle = sanitizeDescription(metadata?.title);
  if (metadataTitle) {
    return metadataTitle;
  }
  return sanitizeDescription(entityDescription);
};

const PHOTO_VIDEO_URL_PATTERN =
  /^https?:\/\/(?:www\.)?(?:twitter|x)\.com\/[^/]+\/status\/\d+\/(?:(?:photo|video)\/\d+)(?:\/)?$/i;
const INLINE_PHOTO_VIDEO_REGEX =
  /https?:\/\/(?:www\.)?(?:twitter|x)\.com\/[^/\s]+\/status\/\d+\/(?:(?:photo|video)\/\d+)(?:\/)?/gi;

const cleanupWhitespace = (text: string): string =>
  text
    .replaceAll(/\s{2,}/g, ' ')
    .replaceAll(/\s+\n/g, '\n')
    .replaceAll(/\n\s+/g, '\n')
    .trim();

const isPhotoOrVideoAttachment = (expandedUrl: string | undefined, displayUrl: string | undefined): boolean => {
  if (!expandedUrl) {
    return false;
  }
  if (PHOTO_VIDEO_URL_PATTERN.test(expandedUrl.trim())) {
    return true;
  }
  if (displayUrl?.toLowerCase().startsWith('pic.twitter.com')) {
    return true;
  }
  return false;
};

const stripMediaLinks = (value: string): string => value.replaceAll(INLINE_PHOTO_VIDEO_REGEX, ' ');

/**
 * Get the weighted length of tweet text
 * URLs count as 23 characters, emoji have different weights
 */
export const getTweetLength = (...parameters: Parameters<typeof getTweetLengthAdapter>) =>
  getTweetLengthAdapter(...parameters);
